export interface PendingSignupReceipt {
  email: string;
  /** Só existe quando veio da resposta autoritativa do signup-guard. */
  expiresAt?: string;
  /** Só existe quando foi escolhido e validado no cadastro atual. */
  roleIntent?: 'consumer' | 'professional_owner';
}

interface ReceiptStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
  removeItem(key: string): unknown;
}

export const PENDING_SIGNUP_STORAGE_KEY = 'powerful-connect:auth:pending-signup:v1';

function isReceipt(value: unknown): value is PendingSignupReceipt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.email === 'string'
    && candidate.email.includes('@')
    && (candidate.expiresAt === undefined
      || (typeof candidate.expiresAt === 'string' && Number.isFinite(Date.parse(candidate.expiresAt))))
    && (candidate.roleIntent === undefined
      || candidate.roleIntent === 'consumer'
      || candidate.roleIntent === 'professional_owner')
  );
}

function browserSessionStorage(): ReceiptStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Adapter estreito para o recibo pendente. A cópia em memória permite seguir para `/verify`
 * mesmo quando o browser bloqueia `sessionStorage`; um reload nesse cenário perde o recibo por
 * desenho e a página apresenta o estado genérico.
 */
export function createPendingSignupStore(storage: ReceiptStorage | null = browserSessionStorage()) {
  let memoryValue: string | null = null;

  const removePersisted = () => {
    try {
      storage?.removeItem(PENDING_SIGNUP_STORAGE_KEY);
    } catch {
      // O fallback em memória continua sendo limpo abaixo.
    }
  };

  const clear = () => {
    memoryValue = null;
    removePersisted();
  };

  return {
    write(receipt: PendingSignupReceipt): void {
      const serialized = JSON.stringify(receipt);
      memoryValue = serialized;
      try {
        storage?.setItem(PENDING_SIGNUP_STORAGE_KEY, serialized);
      } catch {
        // A navegação da sessão atual continua apoiada pela cópia em memória.
      }
    },

    read(now = new Date()): PendingSignupReceipt | null {
      let serialized = memoryValue;
      try {
        serialized = storage?.getItem(PENDING_SIGNUP_STORAGE_KEY) ?? serialized;
      } catch {
        // Usa o fallback em memória.
      }
      if (!serialized) return null;

      try {
        const parsed: unknown = JSON.parse(serialized);
        if (!isReceipt(parsed) || (parsed.expiresAt && Date.parse(parsed.expiresAt) <= now.getTime())) {
          clear();
          return null;
        }
        memoryValue = serialized;
        return parsed;
      } catch {
        clear();
        return null;
      }
    },

    clear,
  };
}

export const pendingSignupStore = createPendingSignupStore();

export function maskPendingSignupEmail(email: string): string {
  const separator = email.lastIndexOf('@');
  if (separator <= 0 || separator === email.length - 1) return '***';

  const local = email.slice(0, separator);
  const domain = email.slice(separator + 1);
  if (local.length === 1) return `${local}***@${domain}`;
  return `${local[0]}${'*'.repeat(Math.max(3, local.length - 2))}${local.at(-1)}@${domain}`;
}
